/**
 * Floating "Cancel Print Queue" panic button.
 *
 * Mounted once at the App root inside `AdminFloatingChromeGate`. Shows
 * up automatically whenever there's at least one `pending` print job
 * ANYWHERE in the system (across every agent / every PC). Disappears
 * when the queue drains.
 *
 * Use case (per warehouse, 2026-06-19): when someone fires off a
 * bad batch print from any PC, anyone with admin/staff access from
 * any other PC can wipe the queue in one click. Stops the bleed-out
 * across the whole fleet without anyone having to look up agent_ids.
 *
 * Behaviour
 * ─────────
 * • Polls `GET /api/print-queue/global-pending-count` every 3 s.
 * • While `count > 0` → renders a fixed bottom-right pulsing red
 *   pill with the live count.
 * • Click → confirm dialog → `POST /api/print-queue/cancel-all-pending-global`.
 * • Server cancels EVERY pending job and records who/when (audit trail
 *   in `cancelled_by_mobile` / `cancelled_at`).
 * • Button instantly hides after a successful cancel (next poll
 *   confirms count = 0).
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { Printer, X, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 3000;

export default function FloatingCancelPrintButton() {
  const { token: customerToken, user } = useAuth();
  // Staff-only sessions have no customer `token`; fall back to the
  // staff JWT so pure-staff users can still see and use the button.
  const token = customerToken || sessionStorage.getItem('staff_token');
  const [count, setCount] = useState(0);
  const [agents, setAgents] = useState(0);
  const [cancelling, setCancelling] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const pollTimer = useRef(null);

  // ── Auth gate: only ADMIN / STAFF should EVER see this button. ───
  // 2026-06-22 fix: a customer browsing the public site at
  // jaiparasapp.in/search/... saw the "Cancel print queue?" dialog
  // because the gate only checked for a token. Customers have tokens
  // too. Now we hard-gate on `is_admin` OR an active staff session
  // (the StaffDashboard sets a `staff_user` mirror in localStorage).
  //
  // 2026-02-17 fix: `staff_user` is written to **sessionStorage**
  // (see AuthPage.js, StaffLoginPage.js, Navbar.js). Reading only
  // localStorage silently hid the button for pure-staff logins. We
  // now read sessionStorage first and fall back to localStorage for
  // any legacy writers. Also, staff-only sessions have `user=null`,
  // so a staff match no longer requires a truthy `user`.
  let staffUser = null;
  try {
    const raw = sessionStorage.getItem('staff_user') || localStorage.getItem('staff_user');
    if (raw) staffUser = JSON.parse(raw);
  } catch (_) { staffUser = null; }
  const isAdmin = Boolean(user && (user.is_admin || user._is_admin || user.is_staff));
  const isStaff = Boolean(staffUser?.staff_id);
  const isAdminOrStaff = isAdmin || isStaff;
  const enabled = Boolean(token) && isAdminOrStaff;

  // ── Poll the count ───────────────────────────────────────────────
  const poll = useCallback(async () => {
    if (!enabled) return;
    try {
      const { data } = await axios.get(
        `${API}/print-queue/global-pending-count`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCount(Number(data?.count) || 0);
      setAgents(Number(data?.agents) || 0);
    } catch (e) {
      // Silent fail — endpoint is allowed to 401/404 in odd states;
      // we just hide the button until the next successful poll.
      setCount(0);
    }
  }, [enabled, token]);

  useEffect(() => {
    if (!enabled) return undefined;
    poll();
    pollTimer.current = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
    };
  }, [enabled, poll]);

  // ── Cancel handler ───────────────────────────────────────────────
  const handleCancelConfirmed = async () => {
    if (cancelling) return;
    setCancelling(true);
    try {
      const { data } = await axios.post(
        `${API}/print-queue/cancel-all-pending-global`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const n = Number(data?.cancelled) || 0;
      toast.success(`Cancelled ${n} of your pending print job${n === 1 ? "" : "s"}`, {
        description:
          "Only YOUR prints were cancelled — any other admins' prints are still running.",
      });
      setCount(0);
      setAgents(0);
      setConfirmOpen(false);
      // Force a follow-up poll so the badge updates instantly if
      // any new pendings raced in during the cancel.
      setTimeout(poll, 800);
    } catch (e) {
      toast.error("Could not cancel print queue", {
        description:
          e?.response?.data?.detail ||
          e?.message ||
          "Network error — try again in a moment.",
      });
    } finally {
      setCancelling(false);
    }
  };

  if (!enabled || count <= 0) return null;

  return (
    <>
      {/* ── Floating pill ──────────────────────────────────────── */}
      <button
        type="button"
        data-testid="floating-cancel-print-button"
        onClick={() => setConfirmOpen(true)}
        title={`${count} pending print job${count === 1 ? "" : "s"} that YOU queued across ${agents} printer${agents === 1 ? "" : "s"} — click to cancel`}
        className="fixed z-[9998] bottom-6 right-6 flex items-center gap-2.5 rounded-full bg-red-600 hover:bg-red-700 active:bg-red-800 text-white shadow-2xl shadow-red-500/40 px-4 py-3 font-medium text-sm transition-all hover:scale-[1.03] active:scale-95"
        style={{
          // soft pulsing glow so it's impossible to miss
          animation: "fcpb-pulse 1.8s ease-in-out infinite",
        }}
      >
        <Printer className="w-4 h-4" />
        <X className="w-3.5 h-3.5 -ml-1" strokeWidth={3} />
        <span className="font-semibold tabular-nums">
          {count} pending
        </span>
        <span className="hidden sm:inline text-red-100/90">
          · Cancel all
        </span>
      </button>

      {/* Inline keyframes so we don't need a CSS file change */}
      <style>{`
        @keyframes fcpb-pulse {
          0%, 100% { box-shadow: 0 10px 30px -8px rgba(239, 68, 68, 0.55), 0 0 0 0 rgba(239, 68, 68, 0.45); }
          50%      { box-shadow: 0 10px 35px -6px rgba(239, 68, 68, 0.75), 0 0 0 12px rgba(239, 68, 68, 0.0); }
        }
      `}</style>

      {/* ── Confirm dialog (no shadcn import — minimal modal) ──── */}
      {confirmOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => !cancelling && setConfirmOpen(false)}
          data-testid="floating-cancel-print-confirm-overlay"
        >
          <div
            className="bg-white dark:bg-zinc-900 rounded-xl shadow-2xl max-w-md w-full p-6 border border-zinc-200 dark:border-zinc-800"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center">
                <Printer className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="font-semibold text-lg text-zinc-900 dark:text-zinc-100">
                Cancel print queue?
              </h3>
            </div>
            <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-1">
              This will cancel <span className="font-semibold text-red-600">{count} pending print job{count === 1 ? "" : "s"}</span> across {agents} printer{agents === 1 ? "" : "s"} (every PC).
            </p>
            <p className="text-xs text-zinc-500 dark:text-zinc-500 mb-5">
              Nothing is deleted — the queue is just emptied. Anyone can re-queue prints normally afterwards. Your name + time will be recorded in the audit log.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                data-testid="floating-cancel-print-dismiss"
                onClick={() => setConfirmOpen(false)}
                disabled={cancelling}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-zinc-100 hover:bg-zinc-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-zinc-700 dark:text-zinc-300 transition disabled:opacity-50"
              >
                Keep printing
              </button>
              <button
                type="button"
                data-testid="floating-cancel-print-confirm"
                onClick={handleCancelConfirmed}
                disabled={cancelling}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-red-600 hover:bg-red-700 text-white transition disabled:opacity-60 flex items-center gap-2"
              >
                {cancelling && <Loader2 className="w-4 h-4 animate-spin" />}
                {cancelling ? "Cancelling…" : `Cancel ${count} print${count === 1 ? "" : "s"}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
